/**
 * ReferralLandingPage — 추천 링크 착지 페이지 (C-8, billing 에이전트 추천인 초대 화면이 공유하는
 * `{origin}/r/{code}` 링크의 실제 진입점). 공개 라우트 — 미로그인 방문자가 대부분이라 RequireAuth
 * 밖(공개 라우트 목록)에 둔다.
 *
 * 이미 로그인된 방문자는 코드를 `?ref=`로 붙여 요금제 화면(PricingPlansPage)으로 바로 보낸다 —
 * 그 화면은 이미 `?ref=`를 읽어 startTrial()에 실어 보내는 처리가 돼 있다(billing 쪽 완료분).
 *
 * 미로그인 방문자는 코드를 localStorage에 저장해두고 회원가입 화면(MasterSignupPage 등)으로 보낸다
 * — 그 화면이 제출 시점에 이 값을 꺼내 실제로 join API에 실어 보낸다(2026-09-22 완료).
 *
 * ⚠️ 로그인된 방문자 경로에서는 localStorage에 절대 쓰지 않는다(2026-09-22 검수에서 발견·수정) —
 * 예전엔 로그인 여부와 무관하게 항상 저장해서, 로그인된 사용자가 호기심에 아무 추천 링크나
 * 눌러도 코드가 계속 남아있었다. 그 상태로 로그아웃하고 같은 브라우저(공용 PC 등)에서 전혀 다른
 * 사람이 추천 링크 없이 곧장 회원가입해도, 그 남은 코드가 자동으로 채워져 엉뚱한 사람에게
 * 리워드가 잘못 귀속될 수 있었다. 로그인 방문자는 애초에 `?ref=`로 바로 처리되어 storage가
 * 필요 없으므로, 안 쓰는 경로에서 그냥 안 쓰게 막았다.
 */
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAuthStore } from '@indipia/shared';

/** 회원가입 화면(MasterSignupPage.tsx 등)이 제출 시점에 이 키로 꺼내 쓴다. */
export const REFERRAL_CODE_STORAGE_KEY = 'referralCode';

export function ReferralLandingPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);

  useEffect(() => {
    // 로그인 방문자는 ?ref=로 바로 처리되므로 storage에 남길 필요가 없다 — 남기면 로그아웃 후
    // 같은 브라우저의 다른 가입자에게 코드가 잘못 새어나갈 수 있다(위 클래스 주석 참고).
    if (code && !accessToken) {
      try {
        localStorage.setItem(REFERRAL_CODE_STORAGE_KEY, code);
      } catch {
        // 프라이빗 모드 등으로 저장 실패해도 착지 자체는 계속 진행 — 추천 코드 없이 일반 가입으로 처리됨
      }
    }
    if (accessToken) {
      navigate(code ? `/agents/billing?ref=${encodeURIComponent(code)}` : '/agents/billing', { replace: true });
    } else {
      navigate('/signup', { replace: true });
    }
  }, [code, accessToken, navigate]);

  return null;
}
